import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IconCheck } from "../icons";

/**
 * DISC — a página que o CANDIDATO abre.
 *
 * É o link enviado pelo WhatsApp: /disc/<token>. Não tem login (o token é a
 * credencial), no mesmo desenho da Triagem de Currículo e da Pesquisa de Clima.
 *
 * As questões NÃO vêm de src/disc-modelo.ts: elas são pedidas ao servidor
 * (GET /api/disc/formulario/:token), que devolve só enunciado e alternativas.
 * O gabarito — qual letra soma D, I, S ou C — nunca chega ao navegador; quem
 * corrige é o servidor. Importar o modelo aqui entregaria o teste de bandeja
 * para quem abrisse o código-fonte da página.
 *
 * QUANTAS questões aparecem depende do teste: com o DISC por cargo, cada vaga
 * pode ter o seu questionário (escrito pela IA e revisado pelo RH); quem não
 * tem recebe as 20 gerais do papel. Por isso nada nesta tela conta "20" — o
 * que vale é o tamanho da lista que o servidor mandou.
 *
 * A pessoa também não vê o resultado: o laudo é do RH. A tela de fim só
 * agradece e explica que o retorno vem pelo RH.
 *
 * O envio leva um `envio_id` fixo desta tela (ver novoEnvioId): tocar em
 * "enviar" de novo depois de uma queda de sinal não duplica o candidato.
 */

interface AlternativaPublica {
  /** "A" | "B" | "C" | "D" — a letra impressa no papel. */
  letra: string;
  texto: string;
}

interface QuestaoPublica {
  /** O número da questão neste teste (é ele que volta no envio). */
  n: number;
  enunciado: string;
  alternativas: AlternativaPublica[];
}

interface FormularioDisc {
  nome: string;
  setor: string | null;
  cargo: string | null;
  descricao: string | null;
  /** Instrução ao participante — texto oficial, vem pronto do servidor. */
  instrucao: string;
  questoes: QuestaoPublica[];
}

/** Um problema traduzido para quem está do outro lado do link. */
interface Falha {
  titulo: string;
  detalhe: string;
}

/**
 * Traduz o status HTTP para uma frase que faça sentido para o candidato.
 *
 * O servidor já responde em português, então a mensagem dele é a primeira
 * opção; o mapa abaixo garante que nenhum código conhecido caia em "Erro 410"
 * na cara da pessoa — e que a tela nunca fique branca.
 */
function falhaDoStatus(status: number, doServidor: string): Falha {
  if (status === 404) {
    return {
      titulo: "Link não encontrado",
      detalhe:
        doServidor ||
        "Este link não existe ou foi copiado pela metade. Confira o endereço que o RH enviou.",
    };
  }
  if (status === 410) {
    return {
      titulo: "Teste encerrado",
      detalhe: doServidor || "Este teste não está mais recebendo respostas. Fale com o RH.",
    };
  }
  if (status === 429) {
    return {
      titulo: "Muitos envios seguidos",
      detalhe:
        doServidor ||
        "Chegaram envios demais deste aparelho em pouco tempo. Aguarde alguns minutos e tente de novo.",
    };
  }
  return {
    titulo: "Não foi possível abrir",
    detalhe: doServidor || "Tente novamente em alguns instantes.",
  };
}

/**
 * Identificador desta tentativa de envio — nasce UMA vez por carregamento da
 * tela e vai junto no corpo do POST.
 *
 * É o que deixa o envio idempotente: no celular em obra o sinal cai no meio do
 * caminho, a pessoa toca em "enviar" de novo e, sem esse id, o RH terminaria
 * com o mesmo candidato duas vezes na lista. Com o id repetido o servidor
 * devolve o registro que já gravou em vez de criar outro.
 *
 * `crypto.randomUUID` não existe em WebView antiga; o retorno de reserva não
 * precisa ser criptográfico, só improvável de repetir entre dois envios.
 */
function novoEnvioId(token: string): string {
  // Guardado por LINK, e não só na memória da tela: a queda de sinal costuma
  // terminar com a pessoa recarregando a página ("não foi, vou abrir de novo") —
  // e um id novo a cada carregamento deixaria o candidato duplicado justamente
  // no caso que este mecanismo veio resolver. O sessionStorage dura o que durar
  // a aba, que é a janela de tempo em que isso acontece. WebView com storage
  // bloqueado lança na leitura: aí vale o id só de memória, que já cobre o
  // reenvio na mesma tela.
  const chave = `disc-envio-${token}`;
  try {
    const guardado = sessionStorage.getItem(chave);
    if (guardado) return guardado;
  } catch {
    /* storage bloqueado — segue com um id novo */
  }
  const cripto: { randomUUID?: () => string } | undefined = globalThis.crypto;
  const id =
    typeof cripto?.randomUUID === "function"
      ? cripto.randomUUID()
      : `envio-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  try {
    sessionStorage.setItem(chave, id);
  } catch {
    /* idem */
  }
  return id;
}

/** Casca comum das páginas por link (mesmas classes `aval-*` das outras). */
function Casca({ children }: { children: React.ReactNode }) {
  return (
    <div className="aval-publica">
      <div className="aval-header">
        <span className="aval-brand">SERCAMP</span>
        <span className="aval-brand-sub">DISC — Análise de Perfil Comportamental</span>
      </div>
      <div className="aval-card">{children}</div>
      <p className="aval-rodape">
        RH SERCAMP · não pedimos dados bancários nem pagamento em nenhuma etapa
      </p>
    </div>
  );
}

/**
 * Uma questão com as 4 alternativas.
 *
 * Declarada FORA do componente de página de propósito: componente aninhado é
 * remontado a cada tecla digitada no formulário, e a tela saltaria do lugar.
 *
 * A alternativa é um <label> grande com o <input type="radio"> escondido — o
 * alvo de toque passa a ser a frase inteira, que é o que funciona no celular.
 * A ORDEM é sempre a que o servidor mandou (A, B, C, D do papel): o RH confere
 * a resposta com o impresso, e embaralhar quebraria essa conferência.
 */
function QuestaoCartao({
  questao,
  marcada,
  pendente,
  onEscolher,
  refLi,
}: {
  questao: QuestaoPublica;
  marcada: string | undefined;
  pendente: boolean;
  onEscolher: (letra: string) => void;
  refLi: (el: HTMLLIElement | null) => void;
}) {
  return (
    <li ref={refLi} className={`disc-questao${pendente ? " pendente" : ""}`}>
      <p className="disc-enunciado">
        <span className="disc-num">{questao.n}</span>
        {questao.enunciado}
      </p>
      <div className="disc-alternativas">
        {questao.alternativas.map((alt) => (
          <label key={alt.letra} className={`disc-alt${marcada === alt.letra ? " marcada" : ""}`}>
            <input
              type="radio"
              name={`disc-q${questao.n}`}
              value={alt.letra}
              checked={marcada === alt.letra}
              onChange={() => onEscolher(alt.letra)}
            />
            <span className="disc-alt-letra">{alt.letra}</span>
            <span className="disc-alt-txt">{alt.texto}</span>
          </label>
        ))}
      </div>
      {pendente && <small className="disc-pendente-aviso">Falta marcar esta questão.</small>}
    </li>
  );
}

export default function DiscPublica({ token }: { token: string }) {
  const [form, setForm] = useState<FormularioDisc | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [falha, setFalha] = useState<Falha | null>(null);

  const [candidato, setCandidato] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [vaga, setVaga] = useState("");

  /** Questão (número do papel) → letra marcada. */
  const [respostas, setRespostas] = useState<Record<number, string>>({});
  /** Questão que o envio apontou em branco — some assim que a pessoa marca. */
  const [pendente, setPendente] = useState<number | null>(null);

  const [enviando, setEnviando] = useState(false);
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  /** Aviso do nome em branco — impresso COLADO no campo, não lá no rodapé. */
  const [erroNome, setErroNome] = useState<string | null>(null);
  const [enviado, setEnviado] = useState<string | null>(null);

  /** Para levar a pessoa até o campo/questão que faltou preencher. */
  const nomeRef = useRef<HTMLInputElement>(null);
  const questoesRef = useRef<Record<number, HTMLLIElement | null>>({});

  /**
   * Id desta tentativa de envio, criado uma única vez. Fica em `useRef` para
   * atravessar os re-renders (cada tecla digitada é um) sem virar um id novo a
   * cada toque em "enviar" — se virasse, o reenvio duplicaria o candidato.
   */
  const envioIdRef = useRef<string>("");
  if (!envioIdRef.current) envioIdRef.current = novoEnvioId(token);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setFalha(null);
    try {
      const res = await fetch(`/api/disc/formulario/${encodeURIComponent(token)}`);
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        setFalha(falhaDoStatus(res.status, body?.error ?? ""));
        return;
      }
      const dados = body as FormularioDisc;
      setForm(dados);
      // A vaga pretendida quase sempre é a do próprio teste — o RH cria a
      // aplicação já com o cargo. Vem preenchida, e a pessoa corrige se quiser.
      setVaga(dados.cargo ?? dados.nome ?? "");
    } catch {
      // Aqui só cai queda de rede: o celular perdeu o sinal no meio do caminho.
      setFalha({
        titulo: "Sem conexão",
        detalhe: "Confira a internet do seu aparelho e abra o link de novo.",
      });
    } finally {
      setCarregando(false);
    }
  }, [token]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const questoes = useMemo(() => form?.questoes ?? [], [form]);
  const respondidas = questoes.filter((q) => respostas[q.n]).length;
  const faltam = questoes.length - respondidas;
  /** A primeira questão em branco, na ordem da tela. */
  const primeiraEmBranco = questoes.find((q) => !respostas[q.n])?.n ?? null;

  function irPara(n: number) {
    setPendente(n);
    questoesRef.current[n]?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  function escolher(n: number, letra: string) {
    setRespostas((atuais) => ({ ...atuais, [n]: letra }));
    // Marcou a questão apontada: o destaque de "falta esta" já cumpriu o papel.
    setPendente((atual) => (atual === n ? null : atual));
  }

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setErroEnvio(null);
    setErroNome(null);

    if (!candidato.trim()) {
      // O aviso nasce ENCOSTADO no campo: o rodapé fica a 20 cartões de
      // distância, e a tela ainda rola para cá — ninguém leria a mensagem lá
      // embaixo. O foco leva a pessoa até o campo e o aviso some ao digitar.
      setErroNome("Escreva o seu nome completo para enviar.");
      nomeRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      nomeRef.current?.focus({ preventScroll: true });
      return;
    }
    // O botão de enviar já fica desabilitado enquanto falta questão; esta trava
    // cobre o envio pelo teclado (Enter) e qualquer estado que escape da tela.
    if (primeiraEmBranco != null) {
      setErroEnvio(`Falta responder a questão ${primeiraEmBranco}.`);
      irPara(primeiraEmBranco);
      return;
    }

    setEnviando(true);
    try {
      const res = await fetch(`/api/disc/formulario/${encodeURIComponent(token)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          // O mesmo id em toda tentativa desta tela: reenvio depois de queda de
          // sinal não vira um segundo candidato para o RH.
          envio_id: envioIdRef.current,
          candidato: candidato.trim(),
          email: email.trim(),
          telefone: telefone.trim(),
          vaga_pretendida: vaga.trim(),
          respostas: questoes.map((q) => ({ q: q.n, alternativa: respostas[q.n] })),
        }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        setErroEnvio(falhaDoStatus(res.status, body?.error ?? "").detalhe);
        return;
      }
      // Deu certo: a marca do envio não serve mais e vai embora. Ela é do LINK,
      // não da pessoa — no tablet da recepção o próximo candidato abre a mesma
      // aba, e uma marca sobrando faria o teste dele ser lido como reenvio do
      // anterior. (O servidor também compara o nome, mas quem apaga aqui é quem
      // sabe que o envio terminou.)
      try {
        sessionStorage.removeItem(`disc-envio-${token}`);
      } catch {
        /* storage bloqueado — nada a limpar */
      }
      envioIdRef.current = "";
      setEnviado(body?.mensagem ?? "Respostas enviadas. Obrigado!");
    } catch {
      // Nada do que foi marcado se perde: o estado continua na tela e a pessoa
      // só precisa tocar em enviar de novo quando o sinal voltar. Tocar de novo
      // é seguro — o `envio_id` acima é o mesmo, então o servidor reconhece a
      // tentativa repetida e não grava um segundo candidato.
      setErroEnvio(
        "Não foi possível enviar agora. Confira a internet e toque em enviar de novo — as suas respostas continuam aqui e o reenvio não cria um segundo cadastro.",
      );
    } finally {
      setEnviando(false);
    }
  }

  if (carregando) {
    return (
      <Casca>
        <div className="empty">Abrindo o teste…</div>
      </Casca>
    );
  }

  if (!form) {
    return (
      <Casca>
        <div className="aval-estado">
          <h2>{falha?.titulo ?? "Link indisponível"}</h2>
          <p>{falha?.detalhe ?? "Este link não está mais válido."}</p>
          <p>Peça um link novo ao RH da SERCAMP.</p>
        </div>
      </Casca>
    );
  }

  if (enviado) {
    return (
      <Casca>
        <div className="aval-estado aval-ok">
          <div className="aval-ok-icon">
            <IconCheck size={40} />
          </div>
          <h2>Respostas enviadas!</h2>
          <p>{enviado}</p>
          <p className="disc-fim-extra">
            Não existe resposta certa ou errada — o DISC descreve o seu jeito de trabalhar. O
            resultado é analisado pelo RH da SERCAMP, que dará o retorno para você.
          </p>
        </div>
      </Casca>
    );
  }

  return (
    <Casca>
      <div className="aval-intro">
        <h2 style={{ margin: "0 0 4px" }}>{form.nome}</h2>
        <p className="aval-cargo">
          {[form.cargo, form.setor].filter(Boolean).join(" · ") || "Processo seletivo SERCAMP"}
        </p>
        {form.descricao && <p>{form.descricao}</p>}
        <p>
          São {questoes.length} questões rápidas sobre o seu jeito de agir. Não existe resposta
          certa ou errada.
        </p>
      </div>

      <form onSubmit={enviar} className="disc-form">
        {/* Identificação. O papel do RH pede nome, data e vaga pretendida — a
            data é a do envio e o sistema carimba sozinho, não se pergunta. */}
        <label className={`field${erroNome ? " disc-campo-falta" : ""}`}>
          <span>
            Seu nome completo <span className="req">*</span>
          </span>
          <input
            ref={nomeRef}
            value={candidato}
            onChange={(e) => {
              setCandidato(e.target.value);
              // Começou a digitar: o aviso já cumpriu o papel.
              if (erroNome) setErroNome(null);
            }}
            placeholder="Nome e sobrenome"
            autoComplete="name"
            aria-invalid={erroNome ? true : undefined}
            aria-describedby={erroNome ? "disc-erro-nome" : undefined}
          />
          {/* <small> e não <span>: `.field span` pintaria o aviso de cinza. */}
          {erroNome && (
            <small id="disc-erro-nome" className="disc-campo-erro">
              {erroNome}
            </small>
          )}
        </label>

        <div className="disc-dados">
          <label className="field">
            <span>Vaga pretendida</span>
            <input
              value={vaga}
              onChange={(e) => setVaga(e.target.value)}
              placeholder="Ex.: Motorista Munck"
            />
          </label>
          <label className="field">
            <span>WhatsApp</span>
            <input
              value={telefone}
              onChange={(e) => setTelefone(e.target.value)}
              placeholder="(19) 90000-0000"
              inputMode="tel"
              autoComplete="tel"
            />
          </label>
          <label className="field">
            <span>E-mail</span>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
              inputMode="email"
              autoComplete="email"
            />
          </label>
        </div>

        <div className="disc-instrucao">
          <strong>Instrução ao participante</strong>
          <p>{form.instrucao}</p>
        </div>

        <ol className="disc-questoes">
          {questoes.map((q) => (
            <QuestaoCartao
              key={q.n}
              questao={q}
              marcada={respostas[q.n]}
              pendente={pendente === q.n}
              onEscolher={(letra) => escolher(q.n, letra)}
              refLi={(el) => {
                questoesRef.current[q.n] = el;
              }}
            />
          ))}
        </ol>

        {erroEnvio && <div className="form-error">{erroEnvio}</div>}

        {/* Rodapé grudado embaixo: no celular a lista é longa e o progresso
            precisa estar sempre à vista, senão a pessoa não sabe onde parou.
            O botão "faltam N" é o atalho até a questão em branco — o de enviar
            só acende quando todas estiverem marcadas. */}
        <div className="disc-foot">
          <div className="disc-progresso">
            <span className="aval-contador">
              {respondidas} de {questoes.length} respondidas
            </span>
            <div className="disc-barra" aria-hidden="true">
              <span
                style={{ width: `${questoes.length ? (respondidas / questoes.length) * 100 : 0}%` }}
              />
            </div>
          </div>
          {faltam > 0 && primeiraEmBranco != null && (
            <button type="button" className="btn-secondary btn-sm" onClick={() => irPara(primeiraEmBranco)}>
              {faltam === 1 ? "Falta 1 — ir até ela" : `Faltam ${faltam} — ir à próxima`}
            </button>
          )}
          <button type="submit" className="btn-primary" disabled={enviando || faltam > 0}>
            {enviando ? "Enviando…" : "Enviar respostas"}
          </button>
        </div>
      </form>
    </Casca>
  );
}
